import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  ModalFooter,
  Button,
  Select,
  Flex,
} from "@chakra-ui/react";

import MoneyField from "../MoneyInput";
import { LoanContext } from "providers/loan";
import { useContext, useState } from "react";
import { AccountReceivableModel } from "providers/loans/account-receivable.model";
import { AlertsContext } from "providers/alerts";
import { apiRequest } from "utils/api";
import moment from "moment";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  payment?: AccountReceivableModel | undefined;
}

export function PaymentModal({ isOpen, onClose, onSave, payment }: PaymentModalProps) {
  const { loan, refetch: loadLoan } = useContext(LoanContext);

  const { showSuccess, showError } = useContext(AlertsContext);
  const [loading, setLoading] = useState(false);

  const [description, setDescription] = useState(payment?.description ?? "");
  const [date, setDate] = useState(payment?.payment_date ?? "");
  const [status, setStatus] = useState(payment?.payment_status ?? "");
  const [amount, setAmount] = useState<number | string>(payment?.payment_amount ?? "");

  async function addPayment() {
    setLoading(true);
    await apiRequest("/supabase/accounts_receivable/insert", {
      paymentAmount: amount,
      paymentStatus: status,
      paymentDate: date,
      description: description,
      loan: loan!.id,
    });

    showSuccess({
      title: "Payment Added",
      description: "The payment has been added",
    });

    setLoading(false);
  }

  async function updatePayment() {
    setLoading(true);
    await apiRequest("/supabase/accounts_receivable/update", {
      id: payment!.id,
      paymentAmount: amount,
      paymentStatus: status,
      paymentDate: date,
      description: description,
      loan_id: loan!.id,
    });

    showSuccess({
      title: "Saved Changes",
      description: "The payment details have been updated.",
    });

    setLoading(false);
  }

  async function handleSave() {
    if (!amount || !date || !description || !status) {
      showError({
        title: "Please fill all the required fields",
        description: "Please fill all the required fields",
      });

      return;
    }

    if (moment(date, "YYYY-MM-DD").isBefore(moment(loan?.funding_date, "YYYY-MM-DD"))) {
      showError({
        title: "Invalid Date",
        description: "The payment date must be after the funding date.",
      });
      return;
    }

    setLoading(true);

    if (payment?.id) {
      await updatePayment();
    } else {
      await addPayment();
    }

    onSave();

    onClose();
    await loadLoan();
  }

  const title = payment?.id ? "Edit Payment" : "Add Payment";
  const saveButtonText = payment?.id ? "Update Payment" : "Add Payment";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            {/* DESCRIPTION */}
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Input
                flex="1"
                value={description}
                placeholder="Enter description..."
                onChange={event => setDescription(event.target.value)}
                type="text"
                variant="primary"
                minHeight="2.5rem"
              />
            </FormControl>
            {/* / DESCRIPTION */}

            {/* PAYMENT DATE */}
            <FormControl>
              <FormLabel>Payment Date</FormLabel>
              <Input
                type="date"
                value={date}
                onChange={event => setDate(event.target.value)}
                variant="primary"
              />
            </FormControl>
            {/* / PAYMENT DATE */}

            {/* PAYMENT AMOUNT */}
            <FormControl>
              <FormLabel>Payment Amount</FormLabel>
              <MoneyField value={amount} onValueChange={value => setAmount(value ?? "")} />
            </FormControl>
            {/* / PAYMENT AMOUNT */}

            {/* PAYMENT STATUS */}
            <FormControl>
              <FormLabel>Payment Status</FormLabel>
              <Select
                placeholder="Select option"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
              </Select>
            </FormControl>
            {/* / PAYMENT STATUS */}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={loading}>
            {saveButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
